import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SubjectClass {
  id: string;
  instructor_id: string;
  subject_id: string;
  branch_id: string | null;
  class_name: string;
  start_date: string;
  target_end_date: string | null;
  teaching_completed_date: string | null;
  all_passed_date: string | null;
  status: string;
  total_class_days: number;
  created_at: string;
  notes: string | null;
  instructor?: { id: string; name: string };
  subject?: { id: string; name: string };
  students_count?: number;
}

export interface SubjectClassStudent {
  id: string;
  subject_class_id: string;
  student_id: string;
  subject_status: string;
  subject_completion_date: string | null;
  exam_application_date: string | null;
  exam_date: string | null;
  exam_result: string | null;
  exam_fail_date: string | null;
  attendance_count: number;
  attendance_percentage: number | null;
  student?: { id: string; first_name: string; last_name: string | null; calling_name: string | null };
}

export interface ClassAttendance {
  id: string;
  subject_class_id: string;
  student_id: string;
  class_date: string;
  status: string;
  marked_by: string | null;
}

export const useSubjectClasses = (instructorOnly: boolean = false) => {
  return useQuery({
    queryKey: ["subject-classes", instructorOnly],
    queryFn: async () => {
      let query = supabase
        .from("subject_classes")
        .select(`
          *,
          instructor:instructors(id, name),
          subject:subjects(id, name)
        `)
        .order("created_at", { ascending: false });

      if (instructorOnly) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: instructor } = await supabase
            .from("instructors")
            .select("id")
            .eq("user_id", user.id)
            .maybeSingle();
          
          if (instructor) {
            query = query.eq("instructor_id", instructor.id);
          }
        }
      }

      const { data, error } = await query;
      if (error) throw error;

      // Get student counts for each class
      const classIds = data?.map(c => c.id) || [];
      if (classIds.length > 0) {
        const { data: studentCounts } = await supabase
          .from("subject_class_students")
          .select("subject_class_id")
          .in("subject_class_id", classIds);

        const countMap = new Map<string, number>();
        studentCounts?.forEach(s => {
          countMap.set(s.subject_class_id, (countMap.get(s.subject_class_id) || 0) + 1);
        });

        return data?.map(c => ({
          ...c,
          students_count: countMap.get(c.id) || 0
        })) as SubjectClass[];
      }

      return data as SubjectClass[];
    },
  });
};

export const useSubjectClassDetails = (classId: string) => {
  return useQuery({
    queryKey: ["subject-class", classId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subject_classes")
        .select(`
          *,
          instructor:instructors(id, name),
          subject:subjects(id, name)
        `)
        .eq("id", classId)
        .single();

      if (error) throw error;
      return data as SubjectClass;
    },
    enabled: !!classId,
  });
};

export const useSubjectClassStudents = (classId: string) => {
  return useQuery({
    queryKey: ["subject-class-students", classId],
    queryFn: async () => {
      // First get class students
      const { data: classStudents, error } = await supabase
        .from("subject_class_students")
        .select("*")
        .eq("subject_class_id", classId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      if (!classStudents || classStudents.length === 0) return [];

      // Then fetch student details separately (handles RLS better for managers)
      const studentIds = classStudents.map(cs => cs.student_id);
      const { data: students } = await supabase
        .from("students")
        .select("id, first_name, last_name, calling_name")
        .in("id", studentIds);

      const studentMap = new Map(students?.map(s => [s.id, s]));

      return classStudents.map(cs => ({
        ...cs,
        student: studentMap.get(cs.student_id) || { id: cs.student_id, first_name: "Unknown", last_name: "", calling_name: null }
      })) as SubjectClassStudent[];
    },
    enabled: !!classId,
  });
};

export const useClassAttendance = (classId: string, date?: string) => {
  return useQuery({
    queryKey: ["class-attendance", classId, date],
    queryFn: async () => {
      let query = supabase
        .from("subject_class_attendance")
        .select("*")
        .eq("subject_class_id", classId);

      if (date) {
        query = query.eq("class_date", date);
      }

      const { data, error } = await query.order("class_date", { ascending: false });
      if (error) throw error;
      return data as ClassAttendance[];
    },
    enabled: !!classId,
  });
};

export const useCreateSubjectClass = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (classData: {
      instructor_id: string;
      subject_id: string;
      branch_id?: string;
      class_name: string;
      start_date: string;
      target_end_date?: string;
      notes?: string;
      student_ids: string[];
    }) => {
      const { student_ids, ...classInfo } = classData;

      // Create the class
      const { data: newClass, error: classError } = await supabase
        .from("subject_classes")
        .insert(classInfo)
        .select()
        .single();

      if (classError) throw classError;

      // Add students to the class
      if (student_ids.length > 0) {
        const studentRecords = student_ids.map(student_id => ({
          subject_class_id: newClass.id,
          student_id,
        }));

        const { error: studentsError } = await supabase
          .from("subject_class_students")
          .insert(studentRecords);

        if (studentsError) throw studentsError;
      }

      return newClass;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subject-classes"] });
    },
  });
};

export const useMarkClassAttendance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      classId,
      date,
      attendance,
    }: {
      classId: string;
      date: string;
      attendance: { student_id: string; status: string }[];
    }) => {
      const { data: { user } } = await supabase.auth.getUser();

      // Upsert attendance records
      const records = attendance.map(a => ({
        subject_class_id: classId,
        student_id: a.student_id,
        class_date: date,
        status: a.status,
        marked_by: user?.id,
      }));

      const { error } = await supabase
        .from("subject_class_attendance")
        .upsert(records, { onConflict: "subject_class_id,student_id,class_date" });

      if (error) throw error;

      // Update total_class_days on the class
      const { data: uniqueDates } = await supabase
        .from("subject_class_attendance")
        .select("class_date")
        .eq("subject_class_id", classId);

      const uniqueDatesCount = new Set(uniqueDates?.map(d => d.class_date)).size;

      await supabase
        .from("subject_classes")
        .update({ total_class_days: uniqueDatesCount })
        .eq("id", classId);

      // Update each student's attendance count and percentage
      for (const a of attendance) {
        const { data: studentAttendance } = await supabase
          .from("subject_class_attendance")
          .select("status")
          .eq("subject_class_id", classId)
          .eq("student_id", a.student_id);

        const presentCount = studentAttendance?.filter(att => att.status === "Present").length || 0;
        const percentage = uniqueDatesCount > 0 ? (presentCount / uniqueDatesCount) * 100 : 0;

        await supabase
          .from("subject_class_students")
          .update({
            attendance_count: presentCount,
            attendance_percentage: Math.round(percentage * 100) / 100,
          })
          .eq("subject_class_id", classId)
          .eq("student_id", a.student_id);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["class-attendance", variables.classId] });
      queryClient.invalidateQueries({ queryKey: ["subject-class-students", variables.classId] });
      queryClient.invalidateQueries({ queryKey: ["subject-class", variables.classId] });
      queryClient.invalidateQueries({ queryKey: ["subject-classes"] });
    },
  });
};

export const useUpdateSubjectClass = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      classId,
      updates,
    }: {
      classId: string;
      updates: Partial<SubjectClass>;
    }) => {
      const { error } = await supabase
        .from("subject_classes")
        .update(updates)
        .eq("id", classId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["subject-class", variables.classId] });
      queryClient.invalidateQueries({ queryKey: ["subject-classes"] });
    },
  });
};

export const useUpdateStudentProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      studentClassId,
      updates,
    }: {
      studentClassId: string;
      updates: Partial<SubjectClassStudent>;
    }) => {
      const { error } = await supabase
        .from("subject_class_students")
        .update(updates)
        .eq("id", studentClassId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subject-class-students"] });
    },
  });
};

export const useAddStudentsToClass = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      classId,
      studentIds,
    }: {
      classId: string;
      studentIds: string[];
    }) => {
      const studentRecords = studentIds.map(student_id => ({
        subject_class_id: classId,
        student_id,
      }));

      const { error } = await supabase
        .from("subject_class_students")
        .insert(studentRecords);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["subject-class-students", variables.classId] });
      queryClient.invalidateQueries({ queryKey: ["subject-classes"] });
    },
  });
};
